import FileCopyIcon from "@mui/icons-material/FileCopy";
import React, { FC, useCallback, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import {
  Menu,
  MenuPreview,
  UserGroupCategory,
} from "client/jspPlatformExperiment";
import MenuSectionViewBox from "components/menu/ui/common/MenuSectionViewBox";
import { Box, Paper, PaperProps, Stack } from "@mui/material";
import Icon from "@mui/material/Icon";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CommonButton from "components/common/ui/CommonButton";
import { useNavigate } from "react-router-dom";
import Routes from "models/routes";
import {
  InfoLabel,
  RightAlignInfoLabelSubHeader,
} from "components/common/ui/InfoLabel";
import { menuDetailActions } from "store/slices/menu/menuDetailSlice";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import SpecialMealDialog from "components/menu/ui/menuPublishment/SpecialMealDialog";
import { isMenuPreview } from "models/menu/menu";
import userGroupOptionSelector from "store/selectors/userGroup/userGroupOptionSelector";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface StyledMenuPaperProps extends PaperProps {
  caution?: boolean | undefined;
}

const StyledMenuPaper = styled(Paper).withConfig({
  shouldForwardProp: (prop) => !["caution"].includes(prop),
})<StyledMenuPaperProps>`
  margin: 0.25rem;
  padding: 0 1rem 1.5rem 0;
  border-radius: 0;

  ${(props) => {
    if (props.caution) {
      return css`
        box-shadow:
          0 0.45rem 0.15rem -0.3rem #ffcf84,
          0 0.3rem 0.3rem 0 #ffcf84,
          0 0.15rem 0.75rem 0 #ffcf84;
      `;
    }
    return ``;
  }}
`;

const StyledMenuPaperIcon = styled(Icon)`
  align-items: center;
  color: rgb(100, 100, 100);
`;

const MenuHeader = styled(Box)`
  font-size: 1.3rem;
  font-weight: 100;
  padding: 1rem 1rem 0 1rem;
  color: rgb(100, 100, 100);
`;

interface SiteLabelProps {
  siteColor: string;
}

const SiteLabel = styled(InfoLabel).withConfig({
  shouldForwardProp: (prop) => prop !== "siteColor",
})<SiteLabelProps>`
  background-color: ${(props) => props.siteColor};
  color: white;
  font-weight: 500;
`;

interface MenuViewBoxProps {
  menu: Menu | MenuPreview;
  showSite?: boolean;
  onEdit?: (menuId: number) => void;
}

const MenuViewBox: FC<MenuViewBoxProps> = ({
  menu,
  showSite = false,
  onEdit,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const siteOptions = useAppSelector(
    userGroupOptionSelector.siteUserGroupOptions,
  );
  const siteColorMap = useAppSelector(userGroupOptionSelector.siteColorMap);

  const siteGroups = useMemo(() => {
    if (!showSite) {
      return [];
    }
    // user_groups is typed as required but can be absent at runtime when the
    // menu comes from a source that doesn't fill relations (e.g. a menu loop's
    // nested menu_links), so guard before iterating.
    return (menu.user_groups ?? []).filter(
      (ug) => ug.group_category === UserGroupCategory.SITE,
    );
  }, [showSite, menu.user_groups]);
  const siteTimezone = useMemo(
    () =>
      menu.user_groups?.find(
        (ug) => ug.group_category === UserGroupCategory.SITE,
      )?.site_profile?.timezone,
    [menu.user_groups],
  );
  // menu.serving_datetime is not guaranteed to come in the site's timezone, so
  // convert it explicitly before displaying or handing it off — that way the
  // wall-clock the user sees and what we ship to the special-meal dialog match
  // the site's local time, not whatever offset the server happened to attach.
  const servingDateInSiteZone = useMemo(() => {
    if (!menu.serving_datetime) {
      return null;
    }
    const parsed = dayjs(menu.serving_datetime);
    return siteTimezone ? parsed.tz(siteTimezone) : parsed;
  }, [menu.serving_datetime, siteTimezone]);
  const [isSpecialDialogOpen, setIsSpecialDialogOpen] = useState(false);

  /**
   * Navigate to menu detail page when edit button clicked.
   * If an onEdit callback is provided, delegate to it instead (Ctrl/Cmd-click
   * still opens the standalone edit page in a new tab).
   */
  const onEditClick = useCallback(
    (menuId: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
      const to = `${Routes.MENU_EDIT}/${menuId}`;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        window.open(to, "_blank");
        return;
      }
      if (onEdit) {
        onEdit(menuId);
        return;
      }
      navigate(to, {
        replace: false,
      });
    },
    [navigate, onEdit],
  );

  /**
   * Handle middle-click (wheel click) to open in new tab
   */
  const onEditMouseDown = useCallback(
    (menuId: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
      // Handle middle-click (wheel click) to open in new tab
      if (e.button === 1) {
        e.preventDefault();
        const to = `${Routes.MENU_EDIT}/${menuId}`;
        window.open(to, "_blank");
      }
    },
    [],
  );

  /**
   * Handle make special button click - show dialog
   */
  const onMakeSpecialClick = useCallback(() => {
    setIsSpecialDialogOpen(true);
  }, []);

  /**
   * Handle special meal dialog confirmation
   */
  const handleSpecialMealConfirm = useCallback(
    (reason: string, dateAndTime: string) => {
      // Copy the menu to the menu detail slice and set special date info
      dispatch(
        menuDetailActions.setMenuDetail({
          ...menu,
          name: `[Special] ${dayjs(dateAndTime).format("YYYY-MM-DD HH:mm")}`,
        }),
      );
      dispatch(
        menuDetailActions.setSpecialDateInfo({
          targetMenuLoopId: isMenuPreview(menu) ? menu.menu_loop.id : 0,
          newSpecialDateCreate: {
            description: reason,
            special_date: dateAndTime,
          },
        }),
      );

      // Navigate to menu create by copy page
      navigate(`${Routes.MENU_CREATE}`, {
        replace: false,
      });
    },
    [dispatch, menu, navigate],
  );

  /**
   * Handle special meal dialog close
   */
  const handleSpecialDialogClose = useCallback(() => {
    setIsSpecialDialogOpen(false);
  }, []);

  return (
    <StyledMenuPaper elevation={3} caution={menu.name?.includes("[Preview]")}>
      <MenuHeader>
        <Stack alignItems="center" direction="row" gap={1}>
          <StyledMenuPaperIcon>
            <FileCopyIcon fontSize="small" />
          </StyledMenuPaperIcon>
          {menu.name}
        </Stack>
        <RightAlignInfoLabelSubHeader>
          <InfoLabel>
            Serving Time:{" "}
            {servingDateInSiteZone ? servingDateInSiteZone.format("H:mm") : ""}
            {siteTimezone &&
            siteTimezone !== Intl.DateTimeFormat().resolvedOptions().timeZone
              ? ` (${siteTimezone})`
              : ""}
          </InfoLabel>
          <InfoLabel>Menu ID: {menu.id}</InfoLabel>
          {siteGroups.map((site) => (
            <SiteLabel
              key={site.id}
              siteColor={siteColorMap[site.id] ?? "rgb(160, 160, 160)"}
            >
              {siteOptions[site.id]?.group_name ?? `Site ${site.id}`}
            </SiteLabel>
          ))}
        </RightAlignInfoLabelSubHeader>
        {!menu.name?.includes("[Auto publish]") ? (
          <CommonButton
            text="Edit"
            positive
            onClick={onEditClick(menu.id)}
            onMouseDown={onEditMouseDown(menu.id)}
          />
        ) : null}
        {isMenuPreview(menu) && !!menu.serving_datetime ? (
          <CommonButton
            text="Make special"
            positive
            onClick={onMakeSpecialClick}
          />
        ) : null}
      </MenuHeader>
      {(menu.sections ?? []).map((section) => (
        <MenuSectionViewBox
          key={`${menu.id}-${section.name}`}
          section={section}
        />
      ))}
      <SpecialMealDialog
        open={isSpecialDialogOpen}
        onClose={handleSpecialDialogClose}
        onConfirm={handleSpecialMealConfirm}
        servingDate={
          servingDateInSiteZone?.format("YYYY-MM-DDTHH:mm:ssZ") ??
          menu.serving_datetime!
        }
        timezone={siteTimezone}
      />
    </StyledMenuPaper>
  );
};

export default MenuViewBox;
